"use server";

import { redirect } from "next/navigation";
import { z } from "zod";
import { prisma } from "@/lib/prisma";
import {
  createSessionToken,
  hashPassword,
  setSessionCookie,
  clearSessionCookie,
  verifyPassword,
  requireSession,
} from "@/lib/auth";
import { SUPER_CLAVE } from "@/lib/super-clave";
import type { ActionResult } from "@/lib/types";

const credentialsSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres"),
  name: z.string().min(1).optional(),
});

export async function verifySuperClaveAction(
  clave: string
): Promise<ActionResult> {
  if (clave !== SUPER_CLAVE) {
    return { success: false, error: "Super Clave incorrecta" };
  }
  return { success: true, data: undefined };
}

/** Registro administrativo: crea usuario sin iniciar sesión ni redirigir. */
export async function adminRegisterAction(input: {
  name: string;
  email: string;
  password: string;
  superClave: string;
}): Promise<ActionResult<{ email: string }>> {
  if (input.superClave !== SUPER_CLAVE) {
    return { success: false, error: "Super Clave incorrecta" };
  }

  const parsed = credentialsSchema.safeParse({
    email: input.email,
    password: input.password,
    name: input.name || undefined,
  });

  if (!parsed.success) {
    return {
      success: false,
      error: parsed.error.issues[0]?.message ?? "Datos inválidos",
    };
  }

  const email = parsed.data.email.toLowerCase();
  const { password, name } = parsed.data;
  const existing = await prisma.user.findUnique({ where: { email } });

  if (existing) {
    return { success: false, error: "Ya existe una cuenta con ese email" };
  }

  await prisma.user.create({
    data: {
      email,
      password: await hashPassword(password),
      name: name ?? email.split("@")[0],
    },
  });

  return { success: true, data: { email } };
}

export async function loginAction(formData: FormData): Promise<ActionResult> {
  const parsed = credentialsSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!parsed.success) {
    return { success: false, error: parsed.error.issues[0]?.message ?? "Datos inválidos" };
  }

  const email = parsed.data.email.toLowerCase();
  const { password } = parsed.data;
  const user = await prisma.user.findUnique({ where: { email } });

  if (!user || !(await verifyPassword(password, user.password))) {
    return { success: false, error: "Email o contraseña incorrectos" };
  }

  const token = await createSessionToken({
    id: user.id,
    email: user.email,
    name: user.name,
  });
  await setSessionCookie(token);
  redirect("/drive");
}

export async function logoutAction(): Promise<void> {
  await clearSessionCookie();
  redirect("/login");
}

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Ingresa tu contraseña actual"),
    newPassword: z
      .string()
      .min(6, "La nueva contraseña debe tener al menos 6 caracteres"),
    confirmPassword: z.string().min(1, "Confirma la nueva contraseña"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas nuevas no coinciden",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "La nueva contraseña debe ser distinta a la actual",
    path: ["newPassword"],
  });

export async function changePasswordAction(input: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<ActionResult> {
  try {
    const session = await requireSession();
    const parsed = changePasswordSchema.safeParse(input);

    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Datos inválidos",
      };
    }

    const user = await prisma.user.findUnique({ where: { id: session.id } });
    if (!user) {
      return { success: false, error: "Usuario no encontrado" };
    }

    const valid = await verifyPassword(
      parsed.data.currentPassword,
      user.password
    );
    if (!valid) {
      return { success: false, error: "La contraseña actual es incorrecta" };
    }

    await prisma.user.update({
      where: { id: user.id },
      data: { password: await hashPassword(parsed.data.newPassword) },
    });

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Error al cambiar la contraseña",
    };
  }
}
